<template>
    <v-card
        class="product-list-item pb-4 d-flex flex-column flex-grow-1 h-100"
        elevation="1"
        itemtype="https://schema.org/Product"
        itemscope
    >
        <div
            v-if="brandTitle"
            class="d-none"
            itemtype="https://schema.org/Brand"
            itemprop="brand"
            itemscope
        >
            <meta itemprop="name" :content="brandTitle" />
        </div>

        <slot name="top"></slot>

        <slot name="image">
            <NuxtLink
                :to="to"
                class="product-list-item-image-link d-block position-relative overflow-hidden"
                :class="{ 'pa-4': isCutoutImage }"
            >
                <FullImage
                    class="w-100 d-table"
                    :key="`prod-img-id-${image?.uid || useId()}`"
                    :image-object="image"
                    :type="['small', 'medium']"
                ></FullImage>
            </NuxtLink>
        </slot>

        <div
            class="position-relative d-flex flex-column flex-grow-1"
        >
            <v-divider
                class="mb-4"
            ></v-divider>

            <slot name="options"></slot>

            <NuxtLink
                :to="to"
                class="text-anchor text-decoration-none d-flex flex-column mt-auto px-4"
            >
                <!-- Item Title -->
                <div
                    class="product-list-item-title mt-4 mb-0"
                >
                    <slot name="title">
                        <div
                            class="product-title title-compact"
                        >
                            <h3
                                class="line-height-normal font-weight-medium pa-0 ma-0"
                                :style="{ fontSize: '20px' }"
                            >
                                {{ title }}
                            </h3>
                        </div>
                    </slot>

                    <meta
                        v-if="description"
                        itemprop="description"
                        :content="description"
                    />
                </div>

                <slot name="info"></slot>
            </NuxtLink>
        </div>
    </v-card>
</template>

<script lang="ts" setup>
    import type { FullImage as FullImageType } from '~/types';
    import FullImage from '~/components/media/FullImage/FullImage.vue';

    const props = defineProps<{
        // Link for the image and the title.
        to: string;
        // Rendered by the default image slot.
        image?: Nilish<FullImageType>;
        // Rendered by the default title slot.
        title?: Nilish<string>;
        // schema.org brand / description meta, omitted when not given.
        brandTitle?: Nilish<string>;
        description?: Nilish<string>;
    }>();

    // Transparent cutout images (the only ones that get a drop shadow) read
    // better inset from the card edges; opaque photos run full-bleed.
    const isCutoutImage = computed<boolean>(() => !!props.image?.showDropShadow);
</script>

<style lang="scss">
.product-list-item {
    // ProductTitle centers itself below the md breakpoint; collection cards
    // are left-aligned at every size.
    .product-title.text-center {
        text-align: start !important;
    }

    .product-title .justify-center {
        justify-content: flex-start !important;
    }

    // The theme maps font-weight-regular to 500 (vuetify.scss) and the
    // font-size-6 utility pins 20px, so taglines render exactly like the
    // natural-case title. Force true regular at 16px here.
    .product-title h4.font-weight-regular {
        font-weight: 400 !important;
        font-size: 16px !important;
        margin-top: 6px !important;
    }
}
</style>
